
import { useQuery } from "@tanstack/react-query";
import { TeamMember } from "@/integrations/supabase/types/";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";

interface TeamChatSidebarProps {
  selectedMember: TeamMember | null;
  onSelectMember: (member: TeamMember | null) => void;
}

export const TeamChatSidebar = ({
  selectedMember,
  onSelectMember,
}: TeamChatSidebarProps) => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");

  const { data: members, isLoading } = useQuery({
    queryKey: ["team-members"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_members")
        .select("*")
        .order("first_name", { ascending: true });

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load team members",
          variant: "destructive",
        });
        throw error;
      }
      return data as TeamMember[];
    },
  });

  const filteredMembers = members?.filter(
    (member) =>
      member.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.last_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="w-80 border-r p-4 flex flex-col">
      <div className="relative mb-4">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search team members..."
          className="pl-8"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <ScrollArea className="flex-1">
        <div className="space-y-2">
          <Button
            variant={!selectedMember ? "secondary" : "ghost"}
            className="w-full justify-start"
            onClick={() => onSelectMember(null)}
          >
            # General Chat
          </Button>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-12 bg-muted rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : (
            filteredMembers?.map((member) => (
              <Button
                key={member.id}
                variant={
                  selectedMember?.id === member.id ? "secondary" : "ghost"
                }
                className="w-full justify-start"
                onClick={() => onSelectMember(member)}
              >
                <Avatar className="h-6 w-6 mr-2">
                  <AvatarFallback>
                    {member.first_name[0]}
                    {member.last_name[0]}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">
                  {member.first_name} {member.last_name}
                </span>
              </Button>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
